import { GetAppointment, GetEncounter } from "@/services/fhir";
import { getDocumentEncounterId } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/documentReissueChain";
import { useFhirData } from "@/utils/useFhirData";

export type ReissueEncounterReference = {
  id?: string;
  subject: string;
  requester: {
    reference?: string;
    display?: string;
  };
  appointmentId?: string;
  participant?: any[];
};

function getContextList(resource: any): any[] {
  if (Array.isArray(resource?.context)) return resource.context;
  if (resource?.context) return [resource.context];
  return [];
}

/** Appointment id no context do DocumentReference (quando presente). */
export function getDocumentAppointmentId(resource: any): string | undefined {
  return getContextList(resource).find(
    (ctx: any) =>
      typeof ctx?.reference === "string" &&
      ctx.reference.includes("Appointment"),
  )?.reference?.split("/")?.[1];
}

/**
 * Monta encounterReference no espírito de ClinicalRegisterContext.verifyEncounter,
 * a partir do DocumentReference selecionado para remissão (sem navigate).
 */
export async function buildReissueEncounterReference({
  documentResource,
  patientId,
  practitionerRoleId,
  practitionerDisplay,
}: {
  documentResource: any;
  patientId: string;
  practitionerRoleId?: string;
  practitionerDisplay?: string;
}): Promise<ReissueEncounterReference> {
  const encounterId = getDocumentEncounterId(documentResource);
  let appointmentId = getDocumentAppointmentId(documentResource);

  // Se só temos Encounter, tenta obter Appointment via GetEncounter (por patient + sort)
  if (!appointmentId && encounterId && patientId) {
    try {
      const encBundle = await GetEncounter({
        patientId,
        lastUpdaded: true,
        count: 20,
      });
      const match = encBundle?.entry?.find(
        (e: any) => e?.resource?.id === encounterId,
      )?.resource;
      appointmentId =
        match?.appointment?.[0]?.reference?.split("/")?.[1] ||
        match?.appointment?.reference?.split("/")?.[1];
    } catch (err) {
      console.warn(
        "[buildReissueEncounterReference] falha ao buscar Encounter",
        err,
      );
    }
  }

  let participant: any[] | undefined;
  if (appointmentId && patientId) {
    try {
      const appointment = await GetAppointment({
        ehrrunner: patientId,
        _id: appointmentId,
      });
      const appt = appointment?.entry?.[0]?.resource;
      if (appt) {
        appointmentId = appt.id || appointmentId;
        participant = appt.participant;
      }
    } catch (err) {
      console.warn(
        "[buildReissueEncounterReference] falha ao buscar Appointment",
        err,
      );
    }
  }

  // Fallback subject via participant Patient se necessário
  const subjectFromParticipant = participant
    ? useFhirData
        .findParticipant(participant, "Patient")
        ?.reference?.split("/")
        ?.pop()
        ?.split("-")
        ?.pop()
    : undefined;

  return {
    id: encounterId,
    subject: patientId || subjectFromParticipant || "",
    requester: {
      reference: practitionerRoleId
        ? `PractitionerRole/${practitionerRoleId}`
        : undefined,
      display: practitionerDisplay,
    },
    appointmentId,
    participant,
  };
}
